import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarOff, Trash2 } from "lucide-react";

const WHOLE_DAY = "whole-day";

/** Admin → Availability: close a date or a single slot so nobody can book it. */
export const AvailabilityTab = () => {
  const [slots, setSlots] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState(WHOLE_DAY);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/appointment-blocks");
    setSlots(data.slots);
    setBlocks(data.blocks);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!date) return toast.error("Pick a date to block");
    setBusy(true);
    try {
      await api.post("/admin/appointment-blocks", {
        date,
        time_slot: slot === WHOLE_DAY ? "" : slot,
        reason,
      });
      toast.success("Blocked — the public page no longer offers it");
      setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/appointment-blocks/${id}`);
      toast.success("Opened back up for bookings");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = blocks.filter((b) => b.date >= today);
  const past = blocks.filter((b) => b.date < today);

  return (
    <div className="space-y-5" data-testid="availability-tab">
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="font-display text-2xl flex items-center gap-2 mb-1"><CalendarOff size={18} /> Block a date or slot</h3>
          <p className="text-sm text-muted-foreground mb-5">
            Travelling or taking a day off? Block it here and the Appointments page stops offering it right away.
          </p>
          <div className="grid md:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} className="mt-2 bg-transparent" data-testid="block-date" />
            </div>
            <div>
              <Label className="overline">Slot</Label>
              <Select value={slot} onValueChange={setSlot}>
                <SelectTrigger className="mt-2" data-testid="block-slot"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={WHOLE_DAY}>Whole day</SelectItem>
                  {slots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Reason (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Travel, festival, surgery day…" className="mt-2 bg-transparent" data-testid="block-reason" />
            </div>
            <Button onClick={add} disabled={busy} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="block-add">
              {busy ? "Blocking…" : "Block it"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="font-display text-2xl mb-4">Upcoming closures</h3>
          {upcoming.length === 0 && <p className="text-sm text-muted-foreground" data-testid="blocks-empty">Nothing blocked — every slot is bookable.</p>}
          <div className="space-y-2">
            {upcoming.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-3 border-b border-border pb-2" data-testid={`block-row-${b.id}`}>
                <span className="font-medium text-sm">{b.date}</span>
                <Badge variant="outline" className="text-[10px]">{b.time_slot || "Whole day"}</Badge>
                {b.reason && <span className="text-xs text-muted-foreground">{b.reason}</span>}
                <button onClick={() => remove(b.id)} className="ml-auto text-muted-foreground hover:text-destructive" data-testid={`block-delete-${b.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          {past.length > 0 && (
            <p className="text-xs text-muted-foreground mt-4">{past.length} past closure(s) kept for your records.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AvailabilityTab;
